import { getSessionOrganizationId, requireSessionOrganizationId } from '../../shell/session/sessionOrganization';
import { supabase } from '../../shared/supabase/supabaseClient';
import { getCatalogRamSnapshotCache, appendWebCatalogProductVariantToCache } from './catalogRamSnapshotCache';
import { CatalogBarcodeDuplicateError, CatalogSkuDuplicateError, VariantInputValidationError } from '../domain/catalogErrors';
import { normalizeProductCode, productCodeUniquenessKey, validateBarcode, validateSku } from '../domain/catalogValidation';
import type { ProductVariant } from '../domain/productVariants';
import { submitCommand } from '../../sync/core/submitCommand';
export type ProductVariantMutationInput = {
  productId: string;
  nameSuffix?: string | null;
  sku?: string | null;
  barcode?: string | null;
  attributes?: Record<string, string>;
  price?: number | null;
  imageUrl?: string | null;
};

type ProductVariantRow = {
  id: string;
  organization_id: string;
  product_id: string;
  sku: string | null;
  barcode: string | null;
  name_suffix: string | null;
  attributes_json: string | null;
  price: number | null;
  image_url: string | null;
  is_active: number | boolean | null;
  updated_at?: string | null;
  created_at?: string | null;
};

const parseAttributes = (raw: string | null | Record<string, unknown> | undefined): Record<string, string> => {
  if (!raw) return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(raw)) {
      if (typeof value === 'string') out[key] = value;
      else if (value != null) out[key] = String(value);
    }
    return out;
  }
  try {
    const parsed = JSON.parse(String(raw)) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value === 'string') out[key] = value;
      else if (value != null) out[key] = String(value);
    }
    return out;
  } catch {
    return {};
  }
};

const serializeAttributes = (attributes: Record<string, string> | undefined): string => {
  if (!attributes || Object.keys(attributes).length === 0) return '{}';
  try {
    return JSON.stringify(attributes);
  } catch {
    return '{}';
  }
};

const normalizeVariantInput = (input: ProductVariantMutationInput) => {
  const nameSuffix = input.nameSuffix?.trim() || null;
  const sku = input.sku?.trim() || null;
  const barcode = input.barcode?.trim() || null;
  const attributes = input.attributes ?? {};
  const priceRaw = input.price;
  const price =
    priceRaw == null || Number.isNaN(Number(priceRaw)) || Number(priceRaw) <= 0 ? null : Number(priceRaw);
  const imageUrl = input.imageUrl?.trim() || null;

  if (!nameSuffix && !sku && Object.keys(attributes).length === 0) {
    throw new VariantInputValidationError('Ingresá al menos un sufijo, SKU o atributo para la variante.');
  }

  // Validate SKU/barcode format (same rules as parent products).
  if (sku) {
    const skuErr = validateSku(sku);
    if (skuErr) throw new VariantInputValidationError(skuErr);
  }
  if (barcode) {
    const barcodeNorm = normalizeProductCode(barcode);
    const barcodeErr = validateBarcode(barcodeNorm);
    if (barcodeErr) throw new VariantInputValidationError(barcodeErr);
  }

  return { nameSuffix, sku, barcode, attributes, price, imageUrl };
};

/**
 * H1: Verifica que el SKU de la variante no colisione con otros productos NI otras variantes.
 * Chequea contra `products` (vía assertUniqueSku) y contra `product_variants` en la caché web.
 */
const assertVariantSkuUnique = async (
  organizationId: string,
  sku: string | null,
  excludedVariantId?: string,
) => {
  if (!productCodeUniquenessKey(sku)) return;
  const key = productCodeUniquenessKey(sku)!;

  // Check against other variants in the same organization.
  const dupVariant = (getCatalogRamSnapshotCache()?.productVariants ?? []).find(
    (v) =>
      String(v.organization_id ?? '') === organizationId &&
      v.is_active !== false &&
      productCodeUniquenessKey(String(v.sku ?? '')) === key &&
      String(v.id) !== (excludedVariantId ?? ''),
  );
  if (dupVariant) {
    throw new CatalogSkuDuplicateError('Ya existe una variante con ese SKU.');
  }

  // Also check against parent products (a variant SKU must not collide with a product SKU).
  const dupProduct = (getCatalogRamSnapshotCache()?.products ?? []).find(
    (p) =>
      String(p.organization_id ?? '') === organizationId &&
      productCodeUniquenessKey(String(p.sku ?? '')) === key,
  );
  if (dupProduct) {
    throw new CatalogSkuDuplicateError('Ya existe un producto con ese SKU.');
  }
};

/**
 * H1: Verifica que el barcode de la variante no colisione con otros productos NI otras variantes.
 */
const assertVariantBarcodeUnique = async (
  organizationId: string,
  barcode: string | null,
  excludedVariantId?: string,
) => {
  if (!productCodeUniquenessKey(barcode)) return;
  const key = productCodeUniquenessKey(barcode)!;

  const dupVariant = (getCatalogRamSnapshotCache()?.productVariants ?? []).find(
    (v) =>
      String(v.organization_id ?? '') === organizationId &&
      v.is_active !== false &&
      productCodeUniquenessKey(String(v.barcode ?? '')) === key &&
      String(v.id) !== (excludedVariantId ?? ''),
  );
  if (dupVariant) {
    throw new CatalogBarcodeDuplicateError('Ya existe una variante con ese código de barras.');
  }

  const dupProduct = (getCatalogRamSnapshotCache()?.products ?? []).find(
    (p) =>
      String(p.organization_id ?? '') === organizationId &&
      productCodeUniquenessKey(String(p.barcode ?? '')) === key,
  );
  if (dupProduct) {
    throw new CatalogBarcodeDuplicateError('Ya existe un producto con ese código de barras.');
  }
};

const mapRow = (row: ProductVariantRow): ProductVariant => ({
  id: row.id,
  organizationId: row.organization_id,
  productId: row.product_id,
  sku: row.sku,
  barcode: row.barcode,
  nameSuffix: row.name_suffix,
  attributes: parseAttributes(row.attributes_json),
  price: row.price,
  imageUrl: row.image_url ?? null,
  isActive: row.is_active !== 0 && row.is_active !== false,
});

const variantAttributesSource = (
  row: Record<string, unknown>,
): string | Record<string, unknown> | null | undefined => {
  if (row.attributes_json != null) return String(row.attributes_json);
  if (row.attributes != null && typeof row.attributes === 'object' && !Array.isArray(row.attributes)) {
    return row.attributes as Record<string, unknown>;
  }
  return null;
};

const mapWebVariantRow = (row: Record<string, unknown>, organizationId: string): ProductVariantRow => ({
  id: String(row.id),
  organization_id: organizationId,
  product_id: String(row.product_id ?? ''),
  sku: row.sku != null ? String(row.sku) : null,
  barcode: row.barcode != null ? String(row.barcode) : null,
  name_suffix: row.name_suffix != null ? String(row.name_suffix) : null,
  attributes_json: serializeAttributes(parseAttributes(variantAttributesSource(row))),
  price: row.price != null ? Number(row.price) : null,
  image_url: row.image_url != null ? String(row.image_url) : null,
  is_active: row.is_active !== false,
  updated_at: row.updated_at != null ? String(row.updated_at) : null,
  created_at: row.created_at != null ? String(row.created_at) : null,
});

const PRODUCT_VARIANT_REMOTE_SELECT =
  'id,organization_id,product_id,sku,barcode,name_suffix,attributes,price,image_url,is_active,created_at,updated_at';

/**
 * Lectura directa de `product_variants` (RLS org-scoped, incluye inactivas).
 * El snapshot RPC solo trae is_active=true, así que las variantes inactivas
 * nunca llegan al cache RAM: para editar/reactivar una variante desactivada
 * en otra sesión o dispositivo hay que ir a la tabla.
 */
const fetchProductVariantRowsRemote = async (
  organizationId: string,
  opts: { productId?: string; variantId?: string },
): Promise<ProductVariantRow[]> => {
  let query = supabase
    .from('product_variants')
    .select(PRODUCT_VARIANT_REMOTE_SELECT)
    .eq('organization_id', organizationId);
  if (opts.productId) query = query.eq('product_id', opts.productId);
  if (opts.variantId) query = query.eq('id', opts.variantId);
  const { data, error } = await query.order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []).map((row) =>
    mapWebVariantRow(row as Record<string, unknown>, organizationId),
  );
};

const isInactiveRow = (row: ProductVariantRow): boolean =>
  row.is_active === false || row.is_active === 0;

const sortVariantRowsByCreatedAt = <T extends { id: string; created_at?: string | null }>(rows: T[]): void => {
  // #803: ordenar por created_at para preservar el orden de generación
  rows.sort((a, b) => {
    const ac = String(a.created_at ?? '');
    const bc = String(b.created_at ?? '');
    if (ac !== bc) return ac < bc ? -1 : 1;
    return String(a.id) < String(b.id) ? -1 : 1;
  });
};

/**
 * Resuelve la fila de variante para una mutación. El snapshot/cache solo
 * contiene activas: si la fila no está en cache (p. ej. inactiva cargada en
 * otra sesión) se busca remoto. Sin fallback, reactivar tras reload fallaba
 * con "No encontramos la variante".
 */
const resolveVariantRowForMutation = async (
  variantId: string,
  organizationId: string,
): Promise<ProductVariantRow | null> => {
  const cached = getCatalogRamSnapshotCache()?.productVariants.find(
    (row) => String(row.id) === variantId,
  );
  if (cached) return mapWebVariantRow(cached, organizationId);
  let rows: ProductVariantRow[];
  try {
    rows = await fetchProductVariantRowsRemote(organizationId, { variantId });
  } catch (error) {
    console.warn('[catalog] resolveVariantRowForMutation: fallo lectura remota', error);
    throw new Error('No pudimos cargar la variante. Revisá tu conexión y reintentá.');
  }
  return rows[0] ?? null;
};

const buildSyncPayload = (
  organizationId: string,
  variantId: string,
  productId: string,
  normalized: ReturnType<typeof normalizeVariantInput>,
  timestamp: string,
  isActive: boolean,
  expectedUpdatedAt?: string,
) => ({
  id: variantId,
  organizationId,
  productId,
  sku: normalized.sku,
  barcode: normalized.barcode,
  nameSuffix: normalized.nameSuffix,
  attributes: normalized.attributes,
  price: normalized.price,
  imageUrl: normalized.imageUrl,
  isActive,
  updatedAt: timestamp,
  // H2/H3: Optimistic concurrency — the server RPC can reject the update if
  // the row's updated_at doesn't match this value (stale client edit).
  ...(expectedUpdatedAt ? { expectedUpdatedAt } : {}),
});

export async function listVariantsForProduct(productId: string): Promise<ProductVariant[]> {
  const organizationId = await getSessionOrganizationId();
  if (!organizationId) return [];

  const rows = (getCatalogRamSnapshotCache()?.productVariants ?? []).filter(
          (row) =>
            String(row.product_id ?? '') === productId &&
            String(row.organization_id ?? organizationId) === organizationId &&
            row.is_active !== false,
        );
        // #803: ordenar por created_at para preservar el orden de generación del producto cartesiano
        rows.sort((a, b) => {
          const ac = String((a as { created_at?: string | null }).created_at ?? '');
          const bc = String((b as { created_at?: string | null }).created_at ?? '');
          if (ac !== bc) return ac < bc ? -1 : 1;
          return String(a.id) < String(b.id) ? -1 : 1;
        });
        return rows.map((row) => mapRow(mapWebVariantRow(row, organizationId)));
}

/**
 * #705: Lista variantes inactivas de un producto (para mostrar en el editor
 * y permitir reactivarlas). Solo devuelve variantes de la organización actual.
 */
export async function listInactiveVariantsForProduct(productId: string): Promise<ProductVariant[]> {
  const organizationId = await getSessionOrganizationId();
  if (!organizationId) return [];

  const cachedRows = (getCatalogRamSnapshotCache()?.productVariants ?? [])
    .filter(
      (row) =>
        String(row.product_id ?? '') === productId &&
        String(row.organization_id ?? organizationId) === organizationId &&
        row.is_active === false,
    )
    .map((row) => mapWebVariantRow(row, organizationId));

  // El snapshot RPC solo trae activas, así que tras un reload las inactivas
  // no están en cache: la fuente es la tabla product_variants (RLS por org).
  let remoteRows: ProductVariantRow[] = [];
  const remoteIds = new Set<string>();
  try {
    const remoteAll = await fetchProductVariantRowsRemote(organizationId, { productId });
    remoteRows = remoteAll.filter(isInactiveRow);
    for (const row of remoteAll) remoteIds.add(row.id);
  } catch (error) {
    // Sin red / error de lectura: degradar al cache (cubre desactivaciones
    // hechas en esta misma sesión).
    console.warn('[catalog] listInactiveVariantsForProduct: fallo lectura remota, usando cache', error);
  }

  // Merge: remote es la verdad (si remote la lista como activa, el cache está
  // stale y no se incluye); el cache solo agrega filas que remote no conoce
  // (p. ej. desactivada hace segundos, lag de propagación).
  const rows = [...remoteRows, ...cachedRows.filter((row) => !remoteIds.has(row.id))];
  sortVariantRowsByCreatedAt(rows);
  return rows.map(mapRow);
}

export async function createVariant(input: ProductVariantMutationInput): Promise<string> {
  const organizationId = await requireSessionOrganizationId();
  const normalized = normalizeVariantInput(input);
  // H1: Validate SKU/barcode uniqueness against products AND other variants.
  await assertVariantSkuUnique(organizationId, normalized.sku);
  await assertVariantBarcodeUnique(organizationId, normalized.barcode);
  const id = crypto.randomUUID();
  const timestamp = new Date().toISOString();

  const result = await submitCommand({
          entity: 'product_variant',
          operation: 'insert',
          payload: buildSyncPayload(organizationId, id, input.productId, normalized, timestamp, true),
        });
        if (!result.applied) {
          throw new Error(result.ingestError?.trim() || 'No se pudo crear la variante en el servidor. Reintentá.');
        }
        appendWebCatalogProductVariantToCache({
          id,
          organization_id: organizationId,
          product_id: input.productId,
          sku: normalized.sku,
          barcode: normalized.barcode,
          name_suffix: normalized.nameSuffix,
          attributes_json: normalized.attributes,
          price: normalized.price,
          image_url: normalized.imageUrl,
          is_active: true,
          // #807: cache null so a subsequent updateVariant in the same session
          // doesn't send a client-time expectedUpdatedAt that mismatches server time.
          updated_at: null,
        });
        return id;
}

export async function updateVariant(variantId: string, input: ProductVariantMutationInput): Promise<void> {
  const organizationId = await requireSessionOrganizationId();
  const normalized = normalizeVariantInput(input);
  // H1: Validate SKU/barcode uniqueness, excluding the variant being edited.
  await assertVariantSkuUnique(organizationId, normalized.sku, variantId);
  await assertVariantBarcodeUnique(organizationId, normalized.barcode, variantId);

  const existing = await resolveVariantRowForMutation(variantId, organizationId);
  if (!existing) {
    throw new Error('No encontramos la variante para editar.');
  }
  if (existing.organization_id !== organizationId) {
    throw new Error('Esta variante pertenece a otra organización.');
  }

  const timestamp = new Date().toISOString();
  // H2/H3: Pass the row's current updated_at so the server can reject stale edits.
  const expectedUpdatedAt = existing.updated_at ?? undefined;

  const result = await submitCommand({
          entity: 'product_variant',
          operation: 'update',
          payload: buildSyncPayload(
            organizationId,
            variantId,
            input.productId,
            normalized,
            timestamp,
            existing.is_active !== 0 && existing.is_active !== false,
            expectedUpdatedAt,
          ),
        });
        if (!result.applied) {
          throw new Error(result.ingestError?.trim() || 'No se pudo actualizar la variante en el servidor. Reintentá.');
        }
        // #807: After a successful update, the server sets updated_at to its own
        // v_now (server time), which may differ from our client `timestamp`. If we
        // cache our client timestamp, the next save in the same session will send
        // a stale expectedUpdatedAt and get rejected. Instead, cache null so the
        // next updateVariant call omits expectedUpdatedAt and the RPC skips the
        // concurrency check (safe — the check is optional by design).
        appendWebCatalogProductVariantToCache({
          id: variantId,
          organization_id: organizationId,
          product_id: input.productId,
          sku: normalized.sku,
          barcode: normalized.barcode,
          name_suffix: normalized.nameSuffix,
          attributes_json: normalized.attributes,
          price: normalized.price,
          image_url: normalized.imageUrl,
          is_active: existing.is_active !== 0 && existing.is_active !== false,
          updated_at: null,
        });
        return;
}

export async function deactivateVariant(variantId: string): Promise<void> {
  const organizationId = await requireSessionOrganizationId();

  const existing = await resolveVariantRowForMutation(variantId, organizationId);
  if (!existing) {
    throw new Error('No encontramos la variante para desactivar.');
  }
  if (existing.organization_id !== organizationId) {
    throw new Error('Esta variante pertenece a otra organización.');
  }

  const timestamp = new Date().toISOString();
  const normalized = {
    nameSuffix: existing.name_suffix,
    sku: existing.sku,
    barcode: existing.barcode,
    attributes: parseAttributes(existing.attributes_json),
    price: existing.price,
    imageUrl: existing.image_url ?? null,
  };

  const result = await submitCommand({
          entity: 'product_variant',
          operation: 'delete',
          payload: buildSyncPayload(organizationId, variantId, existing.product_id, normalized, timestamp, false),
        });
        if (!result.applied) {
          throw new Error(result.ingestError?.trim() || 'No se pudo desactivar la variante en el servidor. Reintentá.');
        }
        appendWebCatalogProductVariantToCache({
          id: variantId,
          organization_id: organizationId,
          product_id: existing.product_id,
          sku: existing.sku,
          barcode: existing.barcode,
          name_suffix: existing.name_suffix,
          attributes_json: normalized.attributes,
          price: existing.price,
          image_url: existing.image_url,
          is_active: false,
          // #807: cache null to avoid stale expectedUpdatedAt on next edit.
          updated_at: null,
        });
        return;
}

/**
 * #705: Reactiva una variante previamente desactivada.
 * Usa la operación 'upsert' con isActive: true para que el RPC
 * apply_product_variant_command la reactive.
 */
export async function reactivateVariant(variantId: string): Promise<void> {
  const organizationId = await requireSessionOrganizationId();

  const existing = await resolveVariantRowForMutation(variantId, organizationId);
  if (!existing) {
    throw new Error('No encontramos la variante para reactivar.');
  }
  if (existing.organization_id !== organizationId) {
    throw new Error('Esta variante pertenece a otra organización.');
  }
  if (existing.is_active) {
    return; // ya está activa, no-op
  }

  const timestamp = new Date().toISOString();
  const normalized = {
    nameSuffix: existing.name_suffix,
    sku: existing.sku,
    barcode: existing.barcode,
    attributes: parseAttributes(existing.attributes_json),
    price: existing.price,
    imageUrl: existing.image_url ?? null,
  };

  const result = await submitCommand({
          entity: 'product_variant',
          operation: 'update',
          payload: buildSyncPayload(organizationId, variantId, existing.product_id, normalized, timestamp, true),
        });
        if (!result.applied) {
          throw new Error(result.ingestError?.trim() || 'No se pudo reactivar la variante en el servidor. Reintentá.');
        }
        appendWebCatalogProductVariantToCache({
          id: variantId,
          organization_id: organizationId,
          product_id: existing.product_id,
          sku: existing.sku,
          barcode: existing.barcode,
          name_suffix: existing.name_suffix,
          attributes_json: normalized.attributes,
          price: existing.price,
          image_url: existing.image_url,
          is_active: true,
          // #807: cache null to avoid stale expectedUpdatedAt on next edit.
          updated_at: null,
        });
        return;
}
